/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import { useGlobalContext } from "context/globalContext";
import Card from "components/card";
import Loader from "components/loader/Loader";
import CreateDestinationBtn from "../create-destination/createDestination";
import EditAndDeleteDestination from "../edit-and-delete/editAndDelete";
import FilterDropdown from "views/admin/tables/new-system-prompts/components/filter-dropdown/FilterDropdown";
import {
  getDestinationsAPI,
  getFilterDestinationsAPI,
} from "Api/destinationsAPI";

const DestinationTable = () => {
  const { fetchDestinations, setFetchDestinations, filterData, getFilterdata } =
    useGlobalContext();
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filterTableData, setFilterTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const debounceSearch = useMemo(() => {
    let timeoutId;
    return function (value) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(value);
        setPageIndex(0);
      }, 300);
    };
  }, []);

  const selectedFilterArray = Object.keys(selectedFilters).filter(
    (key) => selectedFilters[key] !== ""
  );

  const fetchFilteredDestinations = useCallback(async () => {
    if (!searchQuery && selectedFilterArray?.length === 0) return;

    let payload;
    if (searchQuery && selectedFilterArray?.length > 0) {
      const filterObj = Object?.keys(selectedFilters)
        ?.filter((key) => selectedFilters[key] !== "")
        ?.reduce((acc, key) => ({ ...acc, [key]: selectedFilters[key] }), {});
      payload = filterObj?.status
        ? {
            searchQuery,
            ...{ ...filterObj, status: filterObj?.status === "Active" },
          }
        : {
            searchQuery,
            ...filterObj,
          };
    }

    if (searchQuery && selectedFilterArray?.length === 0)
      payload = { searchQuery };
    if (!searchQuery && selectedFilterArray?.length > 0) {
      const filterObj = Object?.keys(selectedFilters)
        ?.filter((key) => selectedFilters[key] !== "")
        ?.reduce((acc, key) => ({ ...acc, [key]: selectedFilters[key] }), {});
      payload = filterObj?.status
        ? { ...{ ...filterObj, status: filterObj?.status === "Active" } }
        : {
            ...filterObj,
          };
    }

    const token = localStorage.getItem("jwtToken");
    const skip = pageIndex * 10;
    const limit = 10;

    try {
      const response = await getFilterDestinationsAPI(
        { searchQuery, pageIndex, limit, skip },
        payload,
        setIsLoading,
        token
      );

      if (response) {
        setFilterTableData(response.data);
        setTotalPages(response.totalPages);
        setFetchDestinations(false);
      } else {
        setFilterTableData([]);
      }
    } catch (error) {
      console.error("Error fetching filtered destinations:", error);
    }
  }, [
    searchQuery,
    pageIndex,
    selectedFilterArray?.length,
    selectedFilters,
    setFetchDestinations,
  ]);

  const getDestinations = useCallback(async () => {
    if (!searchQuery && selectedFilterArray.length === 0) {
      const token = localStorage.getItem("jwtToken");

      const skip = 10;
      try {
        setIsLoading(true);
        const response = await getDestinationsAPI(
          {
            skip,
            pageIndex,
          },
          setIsLoading,
          token
        );
        if (response) {
          setTableData(response.data);
          setTotalPages(response?.pagination?.totalPages || 0);
          setFetchDestinations(false);
        } else {
          setTableData([]);
        }
      } catch (error) {
        console.error("Error fetching destinations:", error);
      }
    }
  }, [
    searchQuery,
    selectedFilterArray.length,
    pageIndex,
    setFetchDestinations,
  ]);

  const columnsData = useMemo(
    () => [
      { Header: "Name", accessor: "Name" },
      { Header: "Type", accessor: "Type" },
      { Header: "Title", accessor: "Title" },
      { Header: "Subtitle", accessor: "Subtitle" },
      { Header: "Region", accessor: "Region" },
      { Header: "Actions", accessor: "actions" },
    ],
    []
  );

  const columns = useMemo(() => columnsData, [columnsData]);

  const tableInstance = useTable(
    {
      columns,
      data:
        searchQuery || selectedFilterArray.length > 0
          ? filterTableData
          : tableData,

      manualPagination: true,
      pageCount: totalPages,
      pageIndex,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,

    gotoPage,
  } = tableInstance;

  const filters = filterData?.find(
    (filter) => filter?.table === "destination"
  )?.filters;

  const handleEditDestination = (updatedDestination) => {
    setTableData((prevData) => {
      return prevData.map((destination) =>
        destination.id === updatedDestination.id
          ? updatedDestination
          : destination
      );
    });
  };

  const handlePageChange = (direction) => {
    if (direction === "next" && pageIndex < totalPages - 1) {
      const nextIndex = pageIndex + 1;
      setPageIndex(nextIndex);
      gotoPage(nextIndex);
    } else if (direction === "previous" && pageIndex > 0) {
      const prevIndex = pageIndex - 1;
      setPageIndex(prevIndex);
      gotoPage(prevIndex);
    }
  };

  useEffect(() => {
    fetchFilteredDestinations(pageIndex);
  }, [searchQuery, selectedFilters, pageIndex, fetchFilteredDestinations]);

  useEffect(() => {
    if (fetchDestinations) {
      getDestinations();
      getFilterdata();
    }

    setFetchDestinations(false);
  }, [fetchDestinations, getDestinations, setFetchDestinations, getFilterdata]);

  useEffect(() => {
    if (searchQuery || selectedFilterArray.length > 0) {
      fetchFilteredDestinations();
    }
  }, [searchQuery, selectedFilters, pageIndex, fetchFilteredDestinations]);

  useEffect(() => {
    if (!searchQuery && selectedFilterArray.length === 0) {
      getDestinations();
    }
  }, [
    fetchDestinations,
    pageIndex,
    searchQuery,
    selectedFilterArray.length,
    getDestinations,
  ]);

  return (
    <Card extra={"w-full h-full p-4 sm:overflow-x-auto"}>
      <div className="relative flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex w-full flex-col gap-3 sm:max-w-lg sm:flex-row">
          <div className="relative w-full sm:w-64">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-lg leading-none text-gray-400">
              🔍
            </span>
            <input
              type="search"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-sm transition-all duration-300 ease-in-out hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
              placeholder="Search"
              onChange={(e) => {
                e.preventDefault();
                debounceSearch(e.target.value);
              }}
            />
          </div>

          {filters?.length > 0 && (
            <FilterDropdown
              filters={filters}
              onFilterSelect={(selectedFilters) => {
                setSelectedFilters(selectedFilters);
                setPageIndex(0);
              }}
              finalFilters={selectedFilters}
            />
          )}
        </div>
        <CreateDestinationBtn />
      </div>

      <div className="mt-4 h-full overflow-x-scroll xl:overflow-auto">
        {isLoading && tableData.length === 0 && (
          <div className="flex h-[400px] w-full items-center justify-center">
            <Loader />
          </div>
        )}
        {!isLoading &&
          (searchQuery || selectedFilterArray?.length > 0) &&
          filterTableData.length === 0 && (
            <span className="block w-full text-center">No data found.</span>
          )}
        {!isLoading && tableData.length === 0 && (
          <span className="block w-full text-center">No data found.</span>
        )}

        {((!!tableData.length &&
          !searchQuery &&
          !selectedFilterArray?.length) ||
          (!!filterTableData.length &&
            (searchQuery || selectedFilterArray?.length > 0))) && (
          <table {...getTableProps()} className="w-full">
            <thead>
              {headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pr-20 pb-[10px] text-start"
                    >
                      <p className="w-20 text-xs tracking-wide text-gray-600">
                        {column.render("Header")}
                      </p>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell, index) => {
                      let data = cell.render("Cell");
                      if (cell.column.Header === "Actions") {
                        data = (
                          <EditAndDeleteDestination
                            width="w-[68px]"
                            value={cell.value}
                            cell={cell}
                            onUpdate={handleEditDestination}
                          />
                        );
                      }
                      return (
                        <td
                          className="px-[7px] pt-[14px] pb-[8px] sm:text-[14px]"
                          {...cell.getCellProps()}
                          key={index}
                        >
                          <p className="max-w-fit">{data}</p>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {((!!tableData.length && !searchQuery) ||
        (!!filterTableData.length && searchQuery)) && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => handlePageChange("previous")}
            disabled={pageIndex === 0}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              pageIndex > 0
                ? "bg-blue-500 hover:bg-blue-600"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Previous
          </button>

          <span className="text-sm text-gray-700 dark:text-white">
            Page <strong>{pageIndex + 1}</strong> of{" "}
            <strong>{totalPages}</strong>
          </span>

          <button
            onClick={() => handlePageChange("next")}
            disabled={pageIndex >= totalPages - 1}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              pageIndex < totalPages - 1
                ? "bg-blue-500 hover:bg-blue-600"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </Card>
  );
};

export default DestinationTable;
